import React, { useContext, useEffect, useImperativeHandle, useState } from 'react'
import { withApollo } from 'react-apollo'

import { Spinner } from '@acorns/web-components'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import { useLazyQuery } from '@apollo/react-hooks'
import { pathOr } from 'ramda'
import { compose } from 'recompose'

import {
  trackInputFieldEditingEndedPayload,
  trackRegistrationCreateAccountNextButtonTappedPayload,
} from 'generated/trackingEvents'
import { EmailAddressValidation } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { translateEventProps } from 'utils/analytics'
import {
  IErrorModalProps,
  withApolloErrorHandler,
  withApolloErrorModal,
} from 'utils/Apollo/apolloErrorHandlingProvider'
import { EnvironmentProps, withEnv } from 'utils/environment'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FunnelContext } from 'utils/FunnelContext'
import { getGeneralError } from 'utils/getGeneralError'
import { PromptProps, withPrompt } from 'utils/prompts'

import { errorModal, generateErrorCodeMap } from '../../logical/errors'
import { FormValues } from '../../logical/formikHelpers'
import emailAddressValidationQuery from './gql/emailAddressValidation.query.gql'
import { VerifyEmailModal } from './VerifyEmailModal'
import { StyledButton } from '../../styled-components'
import { emailTypoMessage } from '../../logical/validation'
import { UserContext } from '../../../../providers/UserProvider'
import BeneficiaryPath from 'assets/images/beneficiary.png'

export interface ButtonHandle {
  click: () => void
}

interface EmailValidationQuery {
  emailAddressValidation: EmailAddressValidation
}

interface InnerProps {
  formValues: FormValues
  getSourceAttributes: any
  inputRef: any
  isEmailValid: boolean
  isSubmitting: boolean
  isEmailFieldFocused: boolean
  setErrors: any
  setIsEmailRowDisplayed: (state: boolean) => void
  setIsEmailValid: (state: boolean) => void
  setSubmitEmailCount: (state: number) => void
  submitEmailCount: number
  validateForm: any
  validPromoCode: string
}

type Props = InnerProps & EnvironmentProps & IErrorModalProps & WithFlowRouterProps & PromptProps

const enhance = compose(
  withApolloErrorModal,
  withPrompt,
  withFlowRouter,
  withEnv,
  withApolloErrorHandler('EmailSubmitSession', () => () => false),
  withApollo,
)

const Button = (props: Props) => {
  const analytics = useAnalyticsClient()
  const { setUserData } = useContext(UserContext)
  const { registerProspect } = useContext(FunnelContext)
  const { getRemoteString } = useRemoteStrings()
  const ctaTitle = getRemoteString({ key: 'registration.landing_page.signup.button' })
  const [
    trackInputFieldEditingEndedValidEventFired,
    setTrackInputFieldEditingEndedValidEventFired,
  ] = useState(false)
  const [
    trackInputFieldEditingEndedInvalidEventFired,
    setTrackInputFieldEditingEndedInvalidEventFired,
  ] = useState(false)
  const [validateEmail, { client: apolloClient }] = useLazyQuery<EmailValidationQuery>(
    emailAddressValidationQuery,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      onCompleted: (data) => onValidationComplete(data),
      onError: (error) => onError(error),
    },
  )

  const trackInputFieldEditingEnded = (isValid) => {
    const [eventName, payload] = trackInputFieldEditingEndedPayload({
      isValid: isValid ? 'true' : 'false',
      required: 'true',
      screen: 'registrationCreateAccount',
      fieldName: 'email',
    })

    analytics.track(eventName, payload)
  }

  useEffect(() => {
    if (props.isEmailFieldFocused || props.formValues.email.length === 0) return

    if (props.isEmailValid && !trackInputFieldEditingEndedValidEventFired) {
      trackInputFieldEditingEnded(true)
      setTrackInputFieldEditingEndedValidEventFired(true)
    }

    if (!props.isEmailValid && !trackInputFieldEditingEndedInvalidEventFired) {
      trackInputFieldEditingEnded(false)
      setTrackInputFieldEditingEndedInvalidEventFired(true)
    }
  }, [
    props.isEmailFieldFocused,
    props.isEmailValid,
    trackInputFieldEditingEndedValidEventFired,
    trackInputFieldEditingEndedInvalidEventFired,
  ])

  const onPress = async () => {
    // Keep count of number of email submissions
    props.setSubmitEmailCount(props.submitEmailCount + 1)
    const errorsOnFormValidate = await props.validateForm()
    if (!errorsOnFormValidate.email) {
      props.setErrors({})

      if (props.formValues.email) {
        setUserData({
          email: props.formValues.email,
        })
      }

      const variables = { email: props.formValues.email }
      try {
        // Apollo queries don't emit `onComplete` handler if query results hit cache
        const cacheHit = apolloClient?.readQuery({ query: emailAddressValidationQuery, variables })
        !cacheHit ? validateEmail({ variables }) : onValidationComplete(cacheHit)
      } catch (error) {
        validateEmail({ variables })
      }
    } else {
      props.setIsEmailValid(false)
      props.setErrors({ email: errorsOnFormValidate.email })
    }

    const [eventName, payload] = trackRegistrationCreateAccountNextButtonTappedPayload({
      ctaTitle,
      destination: undefined,
      funnel: undefined,
    })
    analytics.track(
      eventName,
      translateEventProps(
        {
          ...payload,
          email: props.formValues.email,
        },
        props.getSourceAttributes,
      ),
    )
  }
  const handleEmailValidationSubmission = async () => {
    const sourceAttributes = props.getSourceAttributes()
    const promoCode = props.validPromoCode

    const rawSourceAttributes = { ...sourceAttributes.raw }
    delete rawSourceAttributes.promo_code // Mutation only recognize promoCode not promo_code

    await registerProspect(
      props.formValues.email,
      sourceAttributes.raw.s1,
      JSON.stringify(promoCode ? { ...rawSourceAttributes, promoCode } : rawSourceAttributes),
    )
  }
  const onValidationComplete = ({
    emailAddressValidation: { credentialsExist, possibleTypo, suggestion },
  }) => {
    // If credentials don't exist - prompt user to double check email
    // Otherwise, display credentials taken error modal
    if (!credentialsExist) {
      props
        .promptUser(
          VerifyEmailModal({
            analytics,
            handleChange: () =>
              possibleTypo && props.setErrors({ email: emailTypoMessage(suggestion) }),
            handleOk: () =>
              handleEmailValidationSubmission().then(() => {
                props.setIsEmailRowDisplayed(false)
              }),
            strings: {
              title: getRemoteString({ key: 'create_account.email_confirmation.title' }),
              description: getRemoteString({
                key: 'create_account.email_confirmation.body',
                variables: [props.formValues.email],
              }),
              primaryLabel: getRemoteString({ key: 'create_account.email_confirmation.confirm' }),
              secondaryLabel: getRemoteString({ key: 'create_account.email_confirmation.cancel' }),
            },
            getSourceAttributes: props.getSourceAttributes,
          }),
        )
        .then((result) => {
          if (result && typeof result === 'object' && result.handler) {
            result.handler()
          }
        })
    } else {
      const errorCodeMap = generateErrorCodeMap(
        {
          env: props.env,
          closeErrorModal: props.closeErrorModal,
          title: getRemoteString({
            key: 'error.create_account.email_has_already_been_taken.title',
          }),
          message: getRemoteString({
            key: 'error.create_account.email_has_already_been_taken.body',
          }),
          primaryCtaLabel: getRemoteString({
            key: 'error.create_account.email_has_already_been_taken.cta.confirm',
          }),
          secondaryCtaLabel: getRemoteString({
            key: 'error.create_account.email_has_already_been_taken.cta.cancel',
          }),
        },
        props.formValues,
      )
      const errorCode = 'email_has_already_been_taken'
      const { title, message, actions } = errorCodeMap.get(errorCode)
      props.showErrorModal(
        errorModal({
          title,
          message,
          actions,
          errorCode,
          image: BeneficiaryPath,
        }),
      )
    }
  }
  const onError = (error) => {
    const retryHandler = () => props.closeErrorModal()
    const errorCode = pathOr(
      null,
      ['graphQLErrors', 0, 'extensions', 'response', 'body', 'errors', 0],
      error,
    )
    const customErrorCode =
      error.graphQLErrors?.[0]?.name === 'EmailValidationRateExceededError' ? 429 : undefined
    const { title, message, actions, image } = getGeneralError(
      error,
      props,
      retryHandler,
      customErrorCode,
    )
    props.showErrorModal(
      errorModal({
        title,
        message,
        actions,
        errorCode: customErrorCode ?? errorCode,
        image,
      }),
    )
  }
  // Taking ref from props which is triggered on enter keyDown
  useImperativeHandle(props.inputRef, () => ({
    click() {
      onPress()
    },
  }))

  return (
    <StyledButton
      ref={props.inputRef}
      styleType={props.isEmailValid ? undefined : 'invalid'}
      onPress={onPress}
      data-testid={TestIDs.SIGNUP.GET_STARTED_BUTTON}
    >
      {props.isSubmitting ? <Spinner height={20} width={20} white={true} /> : ctaTitle}
    </StyledButton>
  )
}

export const GetStartedButton = enhance(Button)
